import { zodResolver } from '@hookform/resolvers/zod';
import { isFixedProduct, isUsageProduct } from '@weft/services';
import type { FixedProductInput, UsageProductInput } from '@weft/types';
import { isNotNullOrUndefined } from '@weft/utils';
import { ErrorLabel } from 'apps/app/src/components/design/ErrorLabel';
import { withSuspenseBoundary } from 'apps/app/src/components/utility/SuspenseBoundary';
import { useContract } from 'apps/app/src/hooks/requests/use-contract';
import { useValidatedParams } from 'apps/app/src/hooks/use-validated-params';
import { processRateMultiplierPlanProducts } from 'apps/app/src/services/rate-multiplier-product-processor';
import { useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import type { NewPlanForm } from '../../plans/CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../plans/CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/plan-input-form';
import { PlanInputFormSchema } from '../../plans/CreatePlanPage/create-plan-page-components/CreatePlanForm/create-plan-form-types/plan-input-form';
import { CREATE_PLAN_DEFAULT_VALUES } from '../../plans/CreatePlanPage/create-plan-page-consts/create-plan-default-values';

const ContractReviseForm: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { contractId } = useValidatedParams('contractId');

  const { data: { contract }, isSuccess } = useContract(contractId, {
    refetchOnWindowFocus: false,
  });

  const methods = useForm<PlanInputForm, NewPlanForm>({
    resolver: zodResolver(PlanInputFormSchema),
    defaultValues: CREATE_PLAN_DEFAULT_VALUES,
  });

  useEffect(() => {
    if (isSuccess) {
      const { products: planProducts, ...planWithoutProducts } = contract.plan;
      const rateAdjustedProducts = processRateMultiplierPlanProducts(planProducts);
      const filteredFixedProducts = rateAdjustedProducts.filter(isFixedProduct) as FixedProductInput[];
      const filteredUsageProducts = rateAdjustedProducts.filter(isUsageProduct) as UsageProductInput[];

      methods.reset({
        ...planWithoutProducts,
        fixedProducts: filteredFixedProducts,
        usageProducts: filteredUsageProducts,
      }, {
        keepDefaultValues: true,
      });
    }
  }, [isSuccess, contract.plan, methods]);

  return (
    <FormProvider {...methods}>
      <form className="flex h-full flex-col">
        {children}
      </form>
      {isNotNullOrUndefined(methods.formState.errors.root) && (
        <ErrorLabel
          errorMessage={methods.formState.errors.root?.message ?? ''}
        />
      )}
    </FormProvider>
  );
};

const ContractReviseFormWithSuspenseBoundary = withSuspenseBoundary((ContractReviseForm));
export { ContractReviseFormWithSuspenseBoundary as ContractReviseForm };